import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, KeyRound, Loader2, LockKeyhole, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { PasswordInput } from "@/components/PasswordInput";

const ROLE_PARAM_MAP = { client: "CLIENT", partner: "PARTNER", company: "COMPANY" };
const ROLE_LABELS = { CLIENT: "Client", PARTNER: "Partenaire", COMPANY: "Entreprise", CENTRAL: "Centrale" };
const SPACE_COPY = {
  CLIENT: { eyebrow: "Accès privé · Espace Client", title: "Le suivi de votre projet, en un coup d'œil." },
  PARTNER: { eyebrow: "Accès privé · Espace Partenaire", title: "Vos prospects et vos projets, sur votre secteur." },
  COMPANY: { eyebrow: "Accès privé · Espace Entreprise", title: "Vos chantiers et vos interventions, au même endroit." },
};

export default function Phase3AccessPage() {
  const { login, logout } = useAuth();
  const initialResetToken = new URLSearchParams(window.location.search).get("reset") || "";
  const expectedRole = ROLE_PARAM_MAP[new URLSearchParams(window.location.search).get("role") || ""] || null;
  const [form, setForm] = useState({ email: "", password: "" });
  const [mode, setMode] = useState(initialResetToken ? "reset" : "login");
  const [token, setToken] = useState(initialResetToken);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const copy = expectedRole ? SPACE_COPY[expectedRole] : null;

  const submitLogin = async (event) => {
    event.preventDefault(); setLoading(true); setError("");
    try {
      const user = await login(form.email, form.password);
      if (expectedRole && user.role !== expectedRole) {
        await logout();
        setError(`Ce compte est un compte ${ROLE_LABELS[user.role] || user.role}. Utilisez l'espace ${ROLE_LABELS[expectedRole]} pour vous connecter.`);
        return;
      }
      window.location.assign(user.redirect_to || "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const requestReset = async (event) => {
    event.preventDefault(); setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/password-reset/request", { email: form.email });
      setMessage(data.message);
      if (data.demo_reset_token) { setToken(data.demo_reset_token); setMode("reset"); }
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const confirmReset = async (event) => {
    event.preventDefault(); setLoading(true); setError("");
    if (newPassword !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.post("/auth/password-reset/confirm", { token, password: newPassword });
      setMessage(data.message); setMode("login"); setNewPassword(""); setConfirmPassword("");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="phase3-access-page" className="relative min-h-screen overflow-hidden bg-[#F4F1E9] px-5 py-10 text-navy">
      <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-petrol via-cognac to-sage" />
      <div className="mx-auto grid min-h-[calc(100vh-5rem)] max-w-5xl items-center gap-12 lg:grid-cols-[1fr_26rem]">
        <section className="max-w-xl">
          <Logo className="h-10 w-auto" />
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.24em] text-cognac">{copy?.eyebrow || "Accès privé · Phase 3"}</p>
          <h1 className="mt-4 font-display text-4xl font-medium leading-tight sm:text-5xl">{copy?.title || "Vos projets, protégés côté serveur."}</h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-stone-600">Cet accès de validation utilise désormais une session réelle, des rôles vérifiés et des données persistantes.</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <div className="flex gap-3 border-l-2 border-petrol pl-4"><ShieldCheck className="h-5 w-5 shrink-0 text-petrol" /><p className="text-sm font-semibold">Permissions contrôlées pour chaque projet et document.</p></div>
            <div className="flex gap-3 border-l-2 border-cognac pl-4"><LockKeyhole className="h-5 w-5 shrink-0 text-cognac" /><p className="text-sm font-semibold">Fichiers privés servis uniquement après autorisation.</p></div>
          </div>
        </section>

        <section className="border border-stone-200 bg-white p-7 shadow-[0_24px_80px_rgba(40,78,85,0.12)] sm:p-9" data-testid="phase3-access-panel">
          <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-cognac" /><h2 className="font-display text-2xl font-medium">{mode === "login" ? "Connexion" : mode === "forgot" ? "Réinitialiser l’accès" : "Nouveau mot de passe"}</h2></div>
          {mode === "login" && (
            <form onSubmit={submitLogin} className="mt-7 space-y-4" data-testid="phase3-login-form">
              <input data-testid="phase3-login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full border border-stone-200 px-4 py-3 text-sm outline-none focus:border-cognac" />
              <PasswordInput testId="phase3-login-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe" className="w-full border border-stone-200 px-4 py-3 text-sm outline-none focus:border-cognac" iconClassName="text-stone-400 hover:text-cognac" />
              <button data-testid="phase3-login-submit" disabled={loading} className="flex w-full items-center justify-center gap-2 bg-petrol px-5 py-3.5 text-sm font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Se connecter</button>
              <button data-testid="phase3-forgot-open" type="button" onClick={() => { setMode("forgot"); setError(""); }} className="w-full text-center text-xs font-bold text-stone-500 hover:text-cognac">Mot de passe oublié ?</button>
            </form>
          )}
          {mode === "forgot" && (
            <form onSubmit={requestReset} className="mt-7 space-y-4" data-testid="phase3-forgot-form">
              <input data-testid="phase3-forgot-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email du compte" className="w-full border border-stone-200 px-4 py-3 text-sm outline-none focus:border-cognac" />
              <button data-testid="phase3-forgot-submit" disabled={loading} className="w-full bg-petrol px-5 py-3.5 text-sm font-bold text-white disabled:opacity-50">Préparer la réinitialisation</button>
              <button data-testid="phase3-forgot-back" type="button" onClick={() => setMode("login")} className="w-full text-xs font-bold text-stone-500">Retour à la connexion</button>
            </form>
          )}
          {mode === "reset" && (
            <form onSubmit={confirmReset} className="mt-7 space-y-4" data-testid="phase3-reset-form">
              <input data-testid="phase3-reset-token" required value={token} onChange={(e) => setToken(e.target.value)} placeholder="Jeton de réinitialisation" className="w-full border border-stone-200 px-4 py-3 text-sm outline-none focus:border-cognac" />
              <PasswordInput testId="phase3-reset-password" minLength={10} required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Nouveau mot de passe" className="w-full border border-stone-200 px-4 py-3 text-sm outline-none focus:border-cognac" iconClassName="text-stone-400 hover:text-cognac" />
              <PasswordInput testId="phase3-reset-password-confirm" minLength={10} required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirmer le mot de passe" className="w-full border border-stone-200 px-4 py-3 text-sm outline-none focus:border-cognac" iconClassName="text-stone-400 hover:text-cognac" />
              <button data-testid="phase3-reset-submit" disabled={loading} className="w-full bg-petrol px-5 py-3.5 text-sm font-bold text-white disabled:opacity-50">Enregistrer mon mot de passe</button>
            </form>
          )}
          {message && <p data-testid="phase3-auth-message" className="mt-4 bg-sage/10 px-3 py-2 text-xs font-semibold text-sage">{message}</p>}
          {error && <p data-testid="phase3-auth-error" className="mt-4 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{error}</p>}
        </section>
      </div>
      <Link data-testid="phase3-access-back" to="/" className="absolute left-5 top-6 inline-flex items-center gap-1 text-xs font-bold text-stone-500"><ArrowLeft className="h-3.5 w-3.5" /> Site public</Link>
    </div>
  );
}